/**
 * ONDE ESTÃO OS SERVIÇOS, E O QUE O LABORATÓRIO PRODUZIU.
 *
 * O relatório de serviços liderava por FATURADO, e por isso era uma segunda
 * versão do relatório financeiro — mesmos números, outro título. Serviço é
 * trabalho: quem está esperando qual etapa, quantos fecharam, quantas doadoras
 * passaram pela sala, quantas receptoras receberam embrião. Dinheiro saiu daqui
 * inteiro, porque tem uma aba só dele.
 *
 * Tudo se calcula sobre `vw_servicos_resumo`, que traz as etapas como bandeira
 * (`opu_done`, `lab_done`, `te_done`, `dg60_done`) e o contrato em
 * `services_offered`. As duas coisas juntas respondem a pergunta operacional: um
 * serviço que só contratou TE não está "parado na OPU", ele começa na TE.
 */

/**
 * As etapas que a leitura operacional enxerga, na ordem da corrente.
 *
 * É um subconjunto de `SCHEDULE_CHAIN`: a view não carrega protocolo nem DG30,
 * e inventar uma bandeira que ela não tem produziria uma coluna sempre vazia.
 * `requer` é a chave de `services_offered`, a mesma que o cadastro usa.
 */
export const ETAPAS_DA_OPERACAO = [
  { chave: 'opu', rotulo: 'OPU',     bandeira: 'opu_done',  requer: 'opu' },
  { chave: 'fiv', rotulo: 'FIV',     bandeira: 'lab_done',  requer: 'fiv' },
  { chave: 'te',  rotulo: 'TE',      bandeira: 'te_done',   requer: 'te' },
  { chave: 'dg60', rotulo: 'Prenhez', bandeira: 'dg60_done', requer: 'pregnancy_check' },
];

const contratadas = (linha) => {
  const oferecidos = linha?.services_offered || [];
  return ETAPAS_DA_OPERACAO.filter(e => oferecidos.includes(e.requer));
};

/**
 * Em que etapa este serviço está PARADO, ou nada se ele acabou.
 *
 * A primeira contratada que não foi feita. Não é "a última que foi feita mais
 * um": um serviço pode ter a TE lançada e a OPU não, quando o embrião veio de
 * fora — e ali o que falta é a OPU, não a etapa seguinte à TE.
 */
export const etapaAtual = (linha) => {
  const etapas = contratadas(linha);
  if (etapas.length === 0) return null;
  return etapas.find(e => !linha?.[e.bandeira]) || null;
};

/** O serviço fez tudo o que contratou? */
export const concluido = (linha) => {
  const etapas = contratadas(linha);
  return etapas.length > 0 && etapas.every(e => linha?.[e.bandeira]);
};

/**
 * QUANTOS ESTÃO EM CADA ETAPA, e quantos fecharam.
 *
 * Lê como fila de trabalho, não como funil de conversão: a soma das linhas é o
 * total de serviços, porque cada um está em exatamente um lugar. Um funil somaria
 * o mesmo serviço em várias etapas — é outra pergunta, e ela está em
 * `funilDeProducao`.
 *
 * A etapa sem ninguém continua na lista. Zero na OPU é informação: ou ninguém
 * contratou coleta no período, ou tudo já passou dela.
 */
export const filaPorEtapa = (linhas = []) => {
  const fila = ETAPAS_DA_OPERACAO.map(e => ({ ...e, servicos: 0 }));
  let finalizados = 0;
  let semEtapa = 0;

  for (const l of linhas || []) {
    const etapa = etapaAtual(l);
    if (!etapa) {
      // Sem etapa contratada nenhuma — compra de embrião, diária, venda — não é
      // fila de bancada e não pode ser contado como pronto.
      if (contratadas(l).length === 0) semEtapa += 1;
      else finalizados += 1;
      continue;
    }
    fila.find(f => f.chave === etapa.chave).servicos += 1;
  }

  return { fila, finalizados, semEtapa, total: (linhas || []).length };
};

const soma = (linhas, campo) =>
  (linhas || []).reduce((t, l) => t + (Number(l?.[campo]) || 0), 0);

/**
 * O FUNIL DA PRODUÇÃO: oócito virou embrião, embrião virou prenhez.
 *
 * Aqui as linhas SE SOMAM entre si, ao contrário da fila: é o mesmo material
 * atravessando etapas, e o que interessa é quanto sobrevive de uma para a outra.
 *
 * Cada degrau traz a conversão sobre o ANTERIOR, e não sobre o topo. "35% de
 * prenhez" sobre oócito não é uma taxa que alguém usa; sobre transferido, é a
 * taxa de prenhez de verdade. Quem quiser a leitura de ponta a ponta multiplica
 * as duas — e aí sabe que multiplicou.
 */
export const funilDeProducao = (linhas = []) => {
  const degraus = [
    { chave: 'oocitos', rotulo: 'Oócitos', quantidade: soma(linhas, 'oocitos') },
    { chave: 'embrioes', rotulo: 'Embriões', quantidade: soma(linhas, 'embrioes') },
    { chave: 'transferidos', rotulo: 'Transferidos', quantidade: soma(linhas, 'transferidos') },
    { chave: 'prenhes', rotulo: 'Prenhes', quantidade: soma(linhas, 'prenhes') },
  ];

  return degraus.map((d, i) => {
    const anterior = i > 0 ? degraus[i - 1].quantidade : null;
    return {
      ...d,
      // `null` e não zero quando não há base: "0% de aproveitamento" é uma
      // afirmação, e não havia de onde aproveitar.
      conversao: anterior > 0 ? Math.round((d.quantidade / anterior) * 1000) / 10 : null,
      sobre: anterior === null ? null : degraus[i - 1].rotulo,
    };
  });
};

/** Quantos valores distintos, ignorando vazio. */
const distintos = (linhas, campo) =>
  new Set((linhas || []).map(l => l?.[campo]).filter(Boolean)).size;

/**
 * OS NÚMEROS DO ALTO.
 *
 * Doadora e receptora vêm de fora, de conjuntos próprios, porque é lá que o
 * animal existe: `vw_servicos_resumo` conta oócito e embrião, e não sabe de
 * quantos animais eles saíram. Contar aqui a partir do serviço daria o número de
 * serviços, e um mesmo animal aspirado em três serviços apareceria como três.
 *
 * RECEPTORA VEM DA TE, E "CONFERIDA" É OUTRO NÚMERO. A receptora entra no
 * sistema na transferência; o DG é uma conferência posterior que pode nunca
 * acontecer — em produção são 409 receptoras na TE contra 4 com DG lançado. Os
 * dois lado a lado dizem o tamanho da operação E o tamanho do buraco de
 * lançamento, que é exatamente o que ninguém estava vendo.
 */
export const resumoOperacional = (
  linhas = [], { producao = [], receptoras = [], prenhez = [] } = {},
) => {
  const total = (linhas || []).length;
  const feitos = (linhas || []).filter(concluido).length;

  return {
    servicos: total,
    concluidos: feitos,
    // Zero no denominador devolve `null`, e a tela escreve travessão.
    conclusao: total > 0 ? Math.round((feitos / total) * 1000) / 10 : null,
    clientes: distintos(linhas, 'client_id'),
    doadoras: distintos(producao, 'donor_identifier'),
    receptoras: distintos(receptoras, 'recipient_identifier'),
    receptorasConferidas: distintos(prenhez, 'recipient_identifier'),
    oocitos: soma(linhas, 'oocitos'),
    embrioes: soma(linhas, 'embrioes'),
    transferidos: soma(linhas, 'transferidos'),
    prenhes: soma(linhas, 'prenhes'),
  };
};

/**
 * O QUADRO POR CLIENTE, em trabalho e não em dinheiro.
 *
 * Ordena por embriões produzidos: é o que o laboratório entregou. Serviço conta
 * como volume de agenda, e os dois juntos dizem se um cliente de muitos serviços
 * é também de muito embrião — que raramente é a mesma coisa.
 */
export const operacaoPorCliente = (linhas = [], limite = 12) => {
  const mapa = new Map();

  for (const l of linhas || []) {
    const chave = l?.client_id || l?.client_name || '—';
    if (!mapa.has(chave)) {
      mapa.set(chave, {
        chave,
        rotulo: l?.client_name || 'Sem cliente',
        servicos: 0, oocitos: 0, embrioes: 0, transferidos: 0, prenhes: 0, concluidos: 0,
      });
    }
    const g = mapa.get(chave);
    g.servicos += 1;
    g.oocitos += Number(l?.oocitos) || 0;
    g.embrioes += Number(l?.embrioes) || 0;
    g.transferidos += Number(l?.transferidos) || 0;
    g.prenhes += Number(l?.prenhes) || 0;
    if (concluido(l)) g.concluidos += 1;
  }

  return [...mapa.values()]
    .sort((a, b) => b.embrioes - a.embrioes
      || b.servicos - a.servicos
      || a.rotulo.localeCompare(b.rotulo))
    .slice(0, limite);
};

/**
 * SERVIÇOS ABERTOS POR MÊS, em ordem cronológica.
 *
 * Pela data do serviço, e não pela competência do faturamento: aqui a pergunta é
 * de agenda — quanto trabalho entrou em cada mês —, e o mês em que a conta fecha
 * é assunto do relatório financeiro.
 *
 * Mês vazio no meio fica, porque a queda é o que se quer ver.
 */
export const servicosPorMes = (linhas = []) => {
  const mapa = new Map();
  for (const l of linhas || []) {
    const mes = String(l?.service_date || '').slice(0, 7);
    if (mes.length !== 7) continue;
    const g = mapa.get(mes) || { servicos: 0, embrioes: 0, transferidos: 0 };
    g.servicos += 1;
    g.embrioes += Number(l?.embrioes) || 0;
    g.transferidos += Number(l?.transferidos) || 0;
    mapa.set(mes, g);
  }

  const meses = [...mapa.keys()].sort();
  if (meses.length === 0) return [];

  const saida = [];
  let [ano, mes] = meses[0].split('-').map(Number);
  const fim = meses[meses.length - 1];
  for (;;) {
    const atual = `${ano}-${String(mes).padStart(2, '0')}`;
    const g = mapa.get(atual) || { servicos: 0, embrioes: 0, transferidos: 0 };
    saida.push({ chave: atual, ...g });
    if (atual === fim || saida.length > 240) break;
    mes += 1;
    if (mes > 12) { mes = 1; ano += 1; }
  }
  return saida;
};
